import React, { FC, Suspense, useMemo } from "react"
import { BrowserRouter, Route } from "react-router-dom"
import { ErrorBoundary } from "react-error-boundary"

import {
    ColorScheme,
    ColorSchemeProvider,
    DefaultMantineColor,
    MantineProvider,
    MantineTheme,
    MantineThemeOverride,
    Tuple,
} from "@mantine/core"
import { useColorScheme, useLocalStorage } from "@mantine/hooks"

import Home from "./pages/Home"
import SignIn from "./pages/SignIn"
import AppShell from "./components/layouts/app-shell/AppShell"
import { FullscreenLoader } from "./components/fallbacks/FullscreenLoader"
import { AuthenticationProvider } from "./contexts/authentication-context"
import { Notifications } from "@mantine/notifications"
import Devices from "./pages/Devices"
import HTTP404 from "./pages/HTTP404"
import Device from "./pages/Device"
import { DevicesProvider, GroupsProvider } from "./contexts/devices-context"
import Groups from "./pages/Groups"
import Group from "./pages/Group"
import { LanguageProvider } from "./contexts/language-context"
import Settings from "./pages/Settings"
import { UsersProvider } from "./contexts/users-context"
import AppErrorFallback from "./components/fallbacks/AppErrorFallback"
import { StatisticsProvider } from "./contexts/statistics-context"
import { RoutesWithSentry } from "./services/sentry/sentry"
import { CookiesProvider } from "react-cookie"

type ExtendedCustomColors = "gold" | DefaultMantineColor

declare module "@mantine/core" {
    // eslint-disable-next-line
    export interface MantineThemeColorsOverride {
        colors: Record<ExtendedCustomColors, Tuple<string, 10>>
    }
}

const App: FC = () => {
    const preferredColorScheme = useColorScheme()
    const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
        key: "colorScheme",
        defaultValue: preferredColorScheme,
        getInitialValueInEffect: true,
    })
    const theme = useMemo<MantineThemeOverride>(
        () => ({
            primaryColor: "gold",
            primaryShade: 6,
            colors: {
                gold: [
                    "#F8F4EC",
                    "#ECDFCA",
                    "#E0CAA9",
                    "#D4B687",
                    "#C8A165",
                    "#BC8D43",
                    "#967136",
                    "#715428",
                    "#4B381B",
                    "#261C0D",
                ],
            },
            cursorType: 'pointer',
            fontFamily: "'Open Sans', Arial, sans-serif",
            headings: {
                fontFamily: "'Lexend Deca', Verdana, sans-serif",
                sizes: {
                    h1: { fontSize: "2.5rem", fontWeight: 500 },
                    h2: { fontSize: "1.75rem", fontWeight: 200 },
                },
            },
            colorScheme,
            defaultRadius: "lg",
            white: "#FAFAFA",
            black: "#252528",
            components: {
                Card: { defaultProps: { shadow: "sm" } },
                ActionIcon: { defaultProps: { size: 32 } },
                Menu: { defaultProps: { shadow: "lg", width: 180 } },
                Button: {
                    defaultProps: { radius: "xl", p: "0.5rem", px: "xl" },
                    styles: (theme: MantineTheme) => ({
                        label: {
                            lineHeight: 1.3,
                            fontSize: theme.fontSizes.sm,
                            fontFamily: theme.headings.fontFamily,
                            fontWeight: 500,
                        },
                    }),
                },
            },
            globalStyles: theme => ({
                body: {
                    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[1],
                },
            }),
        }),
        [colorScheme],
    )

    const toggleColorScheme = (value?: ColorScheme) =>
        setColorScheme(value ?? (colorScheme === "dark" ? "light" : "dark"))

    return (
        <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
            <title>{`Ecomoni Dashboard ${process.env.NODE_ENV === 'development' ? "| DEV" : ""}`}</title>
            <MantineProvider theme={theme} withNormalizeCSS withGlobalStyles>
                <ErrorBoundary fallback={<AppErrorFallback />}>
                    <LanguageProvider appDefault="en">
                        <CookiesProvider defaultSetOptions={{ path: '/' }}>
                            <AuthenticationProvider>
                                <Suspense fallback={<FullscreenLoader />}>
                                    <BrowserRouter>
                                        <Notifications />
                                        <RoutesWithSentry>
                                            <Route element={<GroupsProvider route />}>
                                                <Route element={<DevicesProvider route />}>
                                                    <Route element={<UsersProvider route />}>
                                                        <Route path="/" element={<AppShell />}>
                                                            <Route element={<StatisticsProvider route />}>
                                                                <Route index element={<Home />} />
                                                            </Route>
                                                            <Route path="devices" element={<Devices />} />
                                                            <Route path="devices/:id" element={<Device />} />
                                                            <Route path="groups" element={<Groups />} />
                                                            <Route path="groups/:id" element={<Group />} />
                                                            <Route path="settings" element={<Settings />} />
                                                        </Route>
                                                    </Route>
                                                </Route>
                                            </Route>
                                            <Route path="/signin" element={<SignIn />} />
                                            <Route path="*" element={<HTTP404 />} />
                                        </RoutesWithSentry>
                                    </BrowserRouter>
                                </Suspense>
                            </AuthenticationProvider>
                        </CookiesProvider>
                    </LanguageProvider>
                </ErrorBoundary>
            </MantineProvider>
        </ColorSchemeProvider>
    )
}

export default App
